import { useEffect } from 'react';
import { useEditorStore } from '../state/editorStore';
import type { Selection } from '../studioModel';

const REGION_KEYS: Record<string, Selection> = {
  '1': 'page',
  '2': 'community',
  '3': 'channels',
  '4': 'chat',
  '5': 'members',
};

/**
 * The studio's keyboard language: Ctrl+Z / Ctrl+Shift+Z (or Ctrl+Y) undo/redo,
 * Ctrl+S force-saves, 1–5 select regions, B toggles banner mode, +/-/0 zoom.
 * Everything is inert while typing in an input — shortcuts never eat text.
 */
export function useStudioShortcuts({ onFlush, onExit }: { onFlush: () => void; onExit: () => void }) {
  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      const target = e.target as HTMLElement | null;
      const typing =
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target?.isContentEditable;

      const store = useEditorStore.getState();
      const mod = e.ctrlKey || e.metaKey;

      if (mod && e.key.toLowerCase() === 's') {
        e.preventDefault();
        onFlush();
        return;
      }
      if (mod && !typing && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        if (e.shiftKey) store.redo();
        else store.undo();
        return;
      }
      if (mod && !typing && e.key.toLowerCase() === 'y') {
        e.preventDefault();
        store.redo();
        return;
      }
      if (typing || mod) return;

      if (REGION_KEYS[e.key]) {
        store.setSelection(REGION_KEYS[e.key]!);
        if (store.mode !== 'community') store.setMode('community');
        return;
      }
      if (e.key.toLowerCase() === 'b') {
        store.setMode(store.mode === 'banner' ? 'community' : 'banner');
        return;
      }
      if (e.key === '+' || e.key === '=') {
        store.setZoom(store.zoom + 0.1);
        return;
      }
      if (e.key === '-') {
        store.setZoom(store.zoom - 0.1);
        return;
      }
      if (e.key === '0') {
        store.setZoom(1);
        store.setPan({ x: 0, y: 0 });
        return;
      }
      if (e.key === 'Escape') {
        onExit();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onFlush, onExit]);
}
